import { validateVatNumber } from '@/lib/vatValidation';
import { Client } from '@/lib/store';
import { ClientFormData, ClientFormValidation, ClientValidationError, ContactPerson } from './types';

export const validateClientForm = (formData: ClientFormData): ClientFormValidation => {
  const errors: Record<string, string> = {};

  // Required fields validation
  if (!formData.name.trim()) {
    errors.name = 'Company name is required';
  }

  if (!formData.email.trim()) {
    errors.email = 'Email is required';
  } else if (!isValidEmail(formData.email)) {
    errors.email = 'Please enter a valid email address';
  }

  if (!formData.country) {
    errors.country = 'Country is required';
  }

  // VAT number validation
  if (formData.vat_number && formData.country) {
    if (!validateVatNumber(formData.vat_number, formData.country)) {
      errors.vat_number = 'Invalid VAT number format for the selected country';
    }
  }

  // VAT override validation
  if (formData.vat_setting === 'override') {
    if (formData.vat_rate < 0 || formData.vat_rate > 100) {
      errors.vat_rate = 'VAT rate must be between 0 and 100';
    }
    if (!formData.vat_override_message.trim()) {
      errors.vat_override_message = 'Override message is required';
    }
  }

  // Contact persons validation
  formData.contacts.forEach((contact, index) => {
    if (!contact.name.trim()) {
      errors[`contact_${index}_name`] = 'Contact name is required';
    }
    if (!contact.email.trim()) {
      errors[`contact_${index}_email`] = 'Contact email is required';
    } else if (!isValidEmail(contact.email)) {
      errors[`contact_${index}_email`] = 'Please enter a valid email address';
    }
  });

  // Check for duplicate contact emails
  const contactEmails = formData.contacts.map(c => c.email.toLowerCase());
  const duplicateEmails = contactEmails.filter((email, index) => 
    email && contactEmails.indexOf(email) !== index
  );
  
  if (duplicateEmails.length > 0) {
    formData.contacts.forEach((contact, index) => {
      if (duplicateEmails.includes(contact.email.toLowerCase())) {
        errors[`contact_${index}_email`] = 'Duplicate email address';
      }
    });
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const validateEmail = (email: string): boolean => {
  return isValidEmail(email);
};

export const validateVatFormat = (vatNumber: string, country: string): boolean => {
  if (!vatNumber || !country) return true; // Optional field
  return validateVatNumber(vatNumber, country);
};

export const validateContactPersons = (contacts: ContactPerson[]): ClientValidationError[] => {
  const errors: ClientValidationError[] = [];

  contacts.forEach((contact, index) => {
    if (!contact.name.trim()) {
      errors.push({
        field: `contact_${index}_name`,
        message: 'Contact name is required',
      });
    }

    if (!contact.email.trim()) {
      errors.push({
        field: `contact_${index}_email`,
        message: 'Contact email is required',
      });
    } else if (!isValidEmail(contact.email)) {
      errors.push({
        field: `contact_${index}_email`,
        message: 'Please enter a valid email address',
      });
    }
  });

  return errors;
};

export const sanitizeFormData = (formData: ClientFormData): ClientFormData => {
  return {
    ...formData,
    name: formData.name.trim(),
    friendly_name: formData.friendly_name?.trim() || '',
    email: formData.email.trim().toLowerCase(),
    address: formData.address.trim(),
    vat_number: formData.vat_number.trim().toUpperCase(),
    vat_override_message: formData.vat_override_message.trim(),
    peppol_id: formData.peppol_id?.trim().toUpperCase() || '',
    peppol_scheme: formData.peppol_scheme || '',
    contacts: formData.contacts.map(contact => ({
      ...contact,
      name: contact.name.trim(),
      email: contact.email.trim().toLowerCase(),
    })),
  };
};

export const getFieldError = (errors: Record<string, string>, field: string): string | undefined => {
  return errors[field];
};

export const hasFieldError = (errors: Record<string, string>, field: string): boolean => {
  return !!errors[field];
};

export const getContactFieldError = (
  errors: Record<string, string>, 
  index: number, 
  field: 'name' | 'email'
): string | undefined => {
  return errors[`contact_${index}_${field}`];
};

export const hasContactFieldError = (
  errors: Record<string, string>, 
  index: number, 
  field: 'name' | 'email'
): boolean => {
  return !!errors[`contact_${index}_${field}`];
};

// Private helper functions
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

// Form data utilities
export const createEmptyClientForm = (): ClientFormData => ({
  firm: null,
  name: '',
  address: '',
  country: '',
  vat_number: '',
  vat_setting: 'normal',
  vat_rate: 0,
  vat_override_message: '',
  email: '',
  client_contacts: [],
  contacts: [],
  friendly_name: '',
  peppol_enabled: false,
  peppol_id: '',
  peppol_scheme: '',
});

export const clientToFormData = (client: Client): ClientFormData => ({
  ...client,
  contacts: client.contacts || [],
  client_contacts: client.client_contacts || [],
});

// Comparison utilities
export const hasFormDataChanged = (
  original: ClientFormData,
  current: ClientFormData
): boolean => {
  return JSON.stringify(sanitizeFormData(original)) !== JSON.stringify(sanitizeFormData(current));
};
